import { useEffect, useMemo, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../context/PageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { IMatterRequest } from '../../interface/matterRequest';
import { useLanguage } from '../../context/LanguageContext';
import { useCurrencySelection } from '../../hooks/useCurrencySelection';
import { ICurrency } from '../../interface/currencies';
import { Radio, RadioGroup, Spin, Text } from '../Ant';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import NoData from '../dashboard/noData';
import { CurrencyEmpty } from '../icons';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption, SortDirection } from '../../interface/common';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

interface CurrencyRadioSelectionProps {
  currencyList: ICurrency[];
  currentValue: string | undefined;
  handleSelectCurrency: (currency: ICurrency) => void;
  appLanguage: string;
  sortDirection: SortDirection;
}

const CurrencyRadioSelection: React.FC<CurrencyRadioSelectionProps> = ({
  currencyList,
  currentValue,
  handleSelectCurrency,
  appLanguage,
  sortDirection,
}) => {
  const sortedCurrencyList = useMemo(
    () =>
      currencyList
        ? [...currencyList].sort((a, b) => {
            const comparison = getTranslationFromTranslatableEntity(
              a.translations,
              appLanguage,
            ).localeCompare(
              getTranslationFromTranslatableEntity(b.translations, appLanguage),
              appLanguage,
            );
            return sortDirection === 'DESC' ? -comparison : comparison;
          })
        : currencyList,
    [currencyList, appLanguage, sortDirection],
  );

  if (!currencyList || currencyList.length === 0) {
    return null;
  }

  return (
    <RadioGroup data-testid="radio-group" className="d-flex fd-column gap-10" value={currentValue}>
      {sortedCurrencyList.map((currency: ICurrency) => (
        <Radio
          key={currency.currencyCode}
          data-testid={`matter-request-currency-radio-${currency?.currencyCode}`}
          value={currency.currencyCode}
          onClick={() => handleSelectCurrency(currency)}
        >
          <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
            {getTranslationFromTranslatableEntity(currency.translations, appLanguage)}
          </Text>
        </Radio>
      ))}
    </RadioGroup>
  );
};

interface NoDataOrLoadingProps {
  currencyList: ICurrency[];
  isCurrenciesLoading: boolean;
  isFetching: boolean;
}

const NoDataOrLoading: React.FC<NoDataOrLoadingProps> = ({
  currencyList,
  isCurrenciesLoading,
  isFetching,
}) => {
  const { t } = useTranslation();

  if (isCurrenciesLoading) {
    return <SkeletonOptionList />;
  }

  if (!currencyList || (currencyList.length === 0 && !isCurrenciesLoading)) {
    return (
      <div className="flex-grow d-flex justify-center items-center">
        <NoData header={t('matterRequest:noCurrencies')} icon={<CurrencyEmpty />} />
      </div>
    );
  }

  if (isFetching) {
    return <Spin data-testid="small-indicator" size="small" />;
  }

  return null;
};

const SelectCurrency = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { language: appLanguage } = useLanguage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const {
    currencyList,
    isCurrenciesLoading,
    isFetching,
    setSearchValue,
    paginationRef,
    hasNextPage,
    refreshCurrencyList,
  } = useCurrencySelection();

  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    // manageMatterRequest.tsx has no discard model — this mirrors its own handleBack
    // (reset + leave). When embedded in the mail flow (transactionMail.tsx) it's not a
    // separate screen on the stack, so only one level needs to be skipped there vs two
    // for the standalone create/edit matter request route.
    updateManageMatterRequest(null, true);
    goBack(undefined, matterRequest?.isFromMailOrAppointment ? 1 : 2);
  };

  const currencySortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'name', label: t('matterRequest:currency'), type: 'text', defaultDirection: 'ASC' },
    ],
    [t],
  );
  const [currencyActiveSort, setCurrencyActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });

  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('matterRequest:selectCurrency')}
        showCross
      />,
    );
  }, []);

  const handleSelectCurrency = (currency: ICurrency) => {
    updateManageMatterRequest({
      ...matterRequest,
      currency: currency?.currencyCode,
      isCurrencySetByUser: true,
    } as unknown as IMatterRequest);
    goBackWithSelection();
  };

  return (
    <CollapsibleListLayout
      testId="currency-selection"
      header={
        <SearchInputWithFilters
          searchTitle={t('matterRequest:searchCurrencies')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          handleRefresh={refreshCurrencyList}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={currencySortOptions}
            activeSort={currencyActiveSort}
            onApply={setCurrencyActiveSort}
          />
        </SearchInputWithFilters>
      }
    >
      <CurrencyRadioSelection
        currencyList={currencyList}
        currentValue={matterRequest?.currency}
        handleSelectCurrency={handleSelectCurrency}
        appLanguage={appLanguage}
        sortDirection={currencyActiveSort.direction}
      />
      {hasNextPage && <div ref={paginationRef} className="loading-indicator" />}
      <NoDataOrLoading
        currencyList={currencyList}
        isCurrenciesLoading={isCurrenciesLoading}
        isFetching={isFetching}
      />
    </CollapsibleListLayout>
  );
};

export default SelectCurrency;
